/**
 * SEAH Wedding — Event Date enforcement.
 *
 * Dawn's product form is `novalidate` and submits via fetch, so the `required`
 * and `min` attributes on the Event Date input are never checked by the
 * browser. This element validates the field in the CAPTURE phase of the
 * document's submit event — which runs before product-form.js's own handler —
 * and blocks add-to-cart when the date is missing or earlier than the minimum.
 */
(function () {
  'use strict';

  var DEFAULT_MIN_DAYS = 14;

  function pad(n) {
    return String(n).padStart(2, '0');
  }

  function toISO(d) {
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  }

  /** Local-date ISO string for today + N days. */
  function isoPlusDays(days) {
    var d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + (days || 0));
    return toISO(d);
  }

  /**
   * Pure validator. Returns null when valid, else 'missing' | 'invalid' | 'too_early'.
   * ISO date strings compare correctly as plain strings.
   */
  function validate(value, minISO) {
    if (!value) return 'missing';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return 'invalid';
    if (minISO && value < minISO) return 'too_early';
    return null;
  }

  window.SeahEventDate = { validate: validate, isoPlusDays: isoPlusDays };

  if (typeof customElements === 'undefined' || customElements.get('seah-event-date')) return;

  customElements.define(
    'seah-event-date',
    class SeahEventDate extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input[type="date"]');
        this.errorEl = this.querySelector('.seah-event-date__error');
        this.form = this.closest('form');
        if (!this.input || !this.form) return;

        this.minDays = parseInt(this.dataset.minDays, 10) || DEFAULT_MIN_DAYS;

        // The server computed `min` in shop time at render; recompute on the
        // client and keep whichever is later (covers cached pages / timezones).
        var clientMin = isoPlusDays(this.minDays);
        if (!this.input.min || clientMin > this.input.min) this.input.min = clientMin;

        this.onSubmit = this.onSubmit.bind(this);
        this.clearError = this.clearError.bind(this);
        document.addEventListener('submit', this.onSubmit, true); // capture: before product-form.js
        this.input.addEventListener('input', this.clearError);
        this.input.addEventListener('change', this.clearError);
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.onSubmit, true);
      }

      onSubmit(evt) {
        if (evt.target !== this.form) return;
        var code = validate(this.input.value, this.input.min);
        if (!code) return;
        evt.preventDefault();
        evt.stopImmediatePropagation();
        this.showError(code);
      }

      showError(code) {
        var msg =
          code === 'too_early'
            ? 'Please choose an event date on or after ' + this.input.min + ' (at least ' + this.minDays + ' days from today).'
            : 'Please choose your event date (at least ' + this.minDays + ' days from today).';
        if (this.errorEl) {
          this.errorEl.textContent = msg;
          this.errorEl.hidden = false;
        }
        this.input.setAttribute('aria-invalid', 'true');
        this.classList.add('seah-event-date--invalid');
        try {
          this.input.scrollIntoView({ block: 'center', behavior: 'smooth' });
        } catch (e) {
          /* older browsers */
        }
        this.input.focus({ preventScroll: true });
      }

      clearError() {
        if (this.errorEl) this.errorEl.hidden = true;
        this.input.removeAttribute('aria-invalid');
        this.classList.remove('seah-event-date--invalid');
      }
    }
  );
})();
